import * as React from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * PasswordInput
 * -------------
 * Drop-in replacement for `<Input type="password" />` with an accessible
 * show/hide toggle. Hidden by default. Accessible Norwegian aria-labels
 * ("Vis passord" / "Skjul passord"). Forwards all standard input props
 * so existing forms, required/minLength/autoComplete/data-testid work
 * unchanged.
 *
 * Does NOT change submission, validation or auth semantics — only flips
 * the input's `type` between `password` and `text` for the duration of
 * the user's session.
 */
const PasswordInput = React.forwardRef(function PasswordInput(
  { className = "", toggleTestId, ...props },
  ref,
) {
  const [shown, setShown] = React.useState(false);
  return (
    <div className="relative">
      <Input
        ref={ref}
        {...props}
        type={shown ? "text" : "password"}
        className={`pr-10 ${className}`.trim()}
      />
      <button
        type="button"
        onClick={() => setShown((v) => !v)}
        aria-label={shown ? "Skjul passord" : "Vis passord"}
        tabIndex={-1}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        data-testid={toggleTestId}
      >
        {shown
          ? <EyeOff className="w-4 h-4" strokeWidth={1.75} />
          : <Eye className="w-4 h-4" strokeWidth={1.75} />}
      </button>
    </div>
  );
});

export default PasswordInput;
